import type { LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { messageInitials } from '@/features/communications/message-format'

/**
 * Round mark beside each message: who it came from at a glance. ``ours`` is anything we sent
 * (staff, automation, the mailbox), ``counterpart`` the other side (initials), ``note`` a record,
 * ``failed`` a mail server's bounce.
 */
export function SenderAvatar({
  tone,
  icon: Icon,
  name,
  label,
}: {
  tone: 'ours' | 'counterpart' | 'note' | 'failed'
  icon?: LucideIcon
  /** The counterpart's name, shown as initials. */
  name?: string
  /** Accessible name when the mark carries meaning the sender line does not. */
  label?: string
}) {
  const base = 'flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold'
  const toneClass = {
    ours: 'bg-usa-blue/10 text-usa-blue dark:bg-usa-blue/25 dark:text-blue-200',
    counterpart: 'bg-muted text-foreground/80',
    note: 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-200',
    failed: 'bg-destructive/10 text-destructive',
  }[tone]
  return (
    <span className={cn(base, toneClass)} aria-hidden={label ? undefined : true} aria-label={label} role={label ? 'img' : undefined}>
      {Icon ? <Icon className="h-3.5 w-3.5" aria-hidden /> : messageInitials(name || '?')}
    </span>
  )
}
